import React, { useState } from 'react';
import Icon from 'components/AppIcon';

export default function DataUploadSection() {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFiles(e?.target?.files);
    }
  };

  const handleFiles = (files) => {
    const newFiles = Array.from(files)?.map((file) => ({
      name: file?.name,
      size: (file?.size / 1024)?.toFixed(2) + ' KB',
      type: file?.type || 'Unknown',
      status: 'pending',
      id: Date.now() + Math.random(),
    }));
    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const supportedFormats = [
    { format: 'CSV', icon: 'FileText', description: 'Comma-separated values' },
    { format: 'Excel', icon: 'Table', description: 'XLSX, XLS spreadsheets' },
    { format: 'JSON', icon: 'Code', description: 'JavaScript Object Notation' },
    { format: 'XML', icon: 'FileCode', description: 'Extensible Markup Language' },
  ];

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="card">
        <h3 className="text-lg font-semibold text-foreground mb-4">Upload Compatibility Data</h3>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-primary/10 rounded-full">
              <Icon name="Upload" size={32} className="text-primary" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1">
                Drag and drop files here
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                or click to browse from your computer
              </p>
              <input
                type="file"
                multiple
                onChange={handleFileInput}
                className="hidden"
                id="file-upload"
                accept=".csv,.xlsx,.xls,.json,.xml"
              />
              <label htmlFor="file-upload" className="btn-primary cursor-pointer inline-flex items-center gap-2">
                <Icon name="FolderOpen" size={18} />
                Browse Files
              </label>
            </div>
          </div>
        </div>

        {/* Supported Formats */}
        <div className="mt-6">
          <h4 className="font-medium text-foreground mb-3">Supported File Formats</h4>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {supportedFormats?.map((format) => (
              <div key={format?.format} className="p-3 bg-accent rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name={format?.icon} size={16} className="text-primary" />
                  <span className="font-medium text-foreground">{format?.format}</span>
                </div>
                <p className="text-xs text-muted-foreground">{format?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Uploaded Files */}
      {uploadedFiles?.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Uploaded Files</h3>
            <button className="btn-secondary flex items-center gap-2">
              <Icon name="Upload" size={18} />
              Process All
            </button>
          </div>
          <div className="space-y-2">
            {uploadedFiles?.map((file) => (
              <div key={file?.id} className="flex items-center justify-between p-3 bg-accent rounded-lg">
                <div className="flex items-center gap-3 flex-1">
                  <Icon name="File" size={20} className="text-primary" />
                  <div>
                    <div className="font-medium text-foreground">{file?.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {file?.size} • {file?.type}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 text-xs rounded">
                    {file?.status}
                  </span>
                  <button className="p-2 hover:bg-background rounded transition-colors">
                    <Icon name="Trash2" size={18} className="text-muted-foreground" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Upload Guidelines */}
      <div className="card bg-blue-500/5 border border-blue-500/20">
        <div className="flex gap-3">
          <Icon name="Info" size={20} className="text-blue-500 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">Upload Guidelines</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Files must contain chemical name, material name, and compatibility rating</li>
              <li>• Maximum file size: 50 MB</li>
              <li>• Use standard CAS numbers for chemical identification</li>
              <li>• Include temperature and concentration data when available</li>
              <li>• Review data mapping before final import</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}